import React, { useEffect, useState } from 'react';
import { useContest } from '../../Context/ContestContext';
import { useNavigate } from 'react-router-dom'; // Added useNavigate
import { Play, Pause, FastForward, Eye, Clock, Users, CheckCircle, HelpCircle, XCircle, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Row, Col, Button, Badge, Card } from 'react-bootstrap';
import Leaderboard from '../../Components/Leaderboard';
import FullScreenLeaderboard from '../../Components/FullScreenLeaderboard';
import CountdownOverlay from '../../Components/CountdownOverlay';
import Swal from 'sweetalert2';

const ModeratorPanel = () => {
  const { 
    gameState, 
    currentQuestion, 
    roomCode,
    players,
    timer,
    isTimerRunning,
    showAnswer,
    revealedAnswer,
    toggleTimer,
    addTime,
    revealAnswer,
    nextQuestion,
    currentQuestionIndex,
    totalQuestions,
    liveStats,
    resetContest, // Added resetContest..
    terminateGame,
    countdown,
    jokerLimit
  } = useContest();

  const navigate = useNavigate(); // Added navigate initialization
  const [isTerminatedManually, setIsTerminatedManually] = useState(false);
  
  useEffect(() => {
    if (countdown !== null) {
      console.log(`[ModeratorPanel] Countdown current value: ${countdown}`);
    }
  }, [countdown]);

  const alertShownRef = React.useRef(false);

  useEffect(() => {
     if (gameState === 'FINISHED' && !alertShownRef.current) {
        alertShownRef.current = true;
        Swal.fire({
           title: isTerminatedManually ? 'Yarışma Sonlandırıldı!' : 'Yarışma Bitti!',
           text: isTerminatedManually ? 'Moderator tarafından yarışma sonlandırıldı.' : 'Tüm sorular tamamlandı.',
           icon: isTerminatedManually ? 'warning' : 'success',
           confirmButtonText: 'Tamam',
           timer: 5000,
           timerProgressBar: true
        }).then(() => {
           if (isTerminatedManually) {
              resetContest();
              navigate('/');
           }
        });
     }
  }, [gameState, isTerminatedManually, navigate, resetContest]);

  const [showMidLeaderboard, setShowMidLeaderboard] = useState(false);

  // Soru değiştiğinde ara liderlik tablosunu kapat
  useEffect(() => {
     setShowMidLeaderboard(false);
  }, [currentQuestionIndex]);

  const handleNextAction = () => {
     // Eğer cevap gösterilmişse ve ara liderlik tablosu henüz açılmamışsa, onu göster
     if (showAnswer && !showMidLeaderboard) {
        setShowMidLeaderboard(true);
     } else {
        // Diğer durumlarda (veya ara liderlik tablosundayken tıklandığında) sıradaki soruya geç
        nextQuestion();
     }
  };

  const handleTerminate = () => {
     setIsTerminatedManually(true);
     terminateGame();
     resetContest();
     navigate('/');
  };

  const isOptionCorrect = (opt, idx) => {
    // Moderator should always see the correct answer, but styling might change based on showAnswer
    const currentCorrectVal = revealedAnswer?.correctAnswer || currentQuestion?.correctAnswer;
    const currentCorrectVals = revealedAnswer?.correctAnswers || currentQuestion?.correctAnswers || [];
    
    // Check if the option object itself has is_correct property (host_question_data often has this)
    if (typeof opt === 'object' && (opt.is_correct === true || opt.is_correct === 1)) {
        return true;
    }

    const val = typeof opt === 'object' ? (opt.id || opt.option_text || opt.text) : opt;
    
    // Check against correctOptionIds from server
    if (revealedAnswer?.correctOptionIds) {
        if (revealedAnswer.correctOptionIds.map(String).includes(String(val)) ||
            (idx !== undefined && revealedAnswer.correctOptionIds.map(String).includes(String(idx)))) {
            return true;
        }
    }

    const valStr = String(val).trim().toLowerCase();
    const letter = idx !== undefined ? ['A', 'B', 'C', 'D'][idx] : null;

    // Check against IDs, Texts, or Letters
    const isMatched = String(val) === String(currentCorrectVal) || 
                     (letter && letter.toLowerCase() === String(currentCorrectVal).trim().toLowerCase()) ||
                     currentCorrectVals.map(String).includes(String(val)) ||
                     currentCorrectVals.map(v => String(v).toLowerCase().trim()).includes(valStr) ||
                     (letter && currentCorrectVals.map(v => String(v).toLowerCase().trim()).includes(letter.toLowerCase()));
    
    return isMatched;
  };

  const correctAnswers = revealedAnswer?.correctAnswers || currentQuestion?.correctAnswers || [];
  const correctAnswer = revealedAnswer?.correctAnswer || currentQuestion?.correctAnswer;

  const getCorrectAnswerText = () => {
    const opts = currentQuestion?.options || currentQuestion?.Options || [];
    if (Array.isArray(correctAnswers) && correctAnswers.length > 0) {
        return correctAnswers.map(ans => {
            const opt = opts.find((o, idx) => {
                const letter = ['A', 'B', 'C', 'D'][idx];
                return String(o.id || idx) === String(ans) || 
                       String(o.option_text || o.text) === String(ans) ||
                       (letter && letter.toLowerCase() === String(ans).trim().toLowerCase());
            });
            return opt ? `${opt.option_text || opt.text}` : ans;
        }).join(' / ');
    }
    
    if (correctAnswer) {
        const opt = opts.find((o, idx) => {
            const letter = ['A', 'B', 'C', 'D'][idx];
            return String(o.id || idx) === String(correctAnswer) || 
                   String(o.option_text || o.text) === String(correctAnswer) ||
                   (letter && letter.toLowerCase() === String(correctAnswer).trim().toLowerCase());
        });
        return opt ? `${opt.option_text || opt.text}` : correctAnswer;
    }
    
    return 'Cevap bulunamadı';
  };

  const renderContent = () => {
    // Sadece oyun bitince liderlik tablosu göster
    if (gameState === 'FINISHED') {
        return (
           <div className="h-100 position-relative">
              <FullScreenLeaderboard players={players} />
              <div className="position-absolute bottom-0 end-0 p-4 z-3">
                 <Button 
                    size="lg" 
                    variant="primary" 
                    onClick={() => { resetContest(); navigate('/'); }} 
                    className="fw-bold shadow-lg d-flex align-items-center gap-2 px-5 py-3 rounded-pill"
                 >
                    Ana Sayfaya Dön
                 </Button>
              </div>
           </div>
        );
    }

    // Ara liderlik tablosu ekranı
    if (showMidLeaderboard) {
       return (
          <div className="h-100 position-relative">
             <FullScreenLeaderboard players={players} />
             <div className="position-absolute bottom-0 end-0 p-4 z-3">
                <Button 
                   size="lg" 
                   variant="dark" 
                   onClick={handleNextAction} 
                   className="fw-bold shadow-lg d-flex align-items-center gap-2 px-5 py-3 rounded-pill"
                >
                   Sıradaki Soru <FastForward size={24} />
                </Button>
             </div>
          </div>
       );
    }

     // Mola ekranı
     if (gameState === 'BREAK_TIME') {
        return (
           <div className="h-100 position-relative" style={{ background: '#0a0f1a' }}>
              {/* Tam Ekran Liderlik Tablosu */}
              <FullScreenLeaderboard players={players} />
              
              {/* Üst Bilgi ve Geri Sayım Paneli */}
              <div className="position-absolute top-0 start-50 translate-middle-x mt-4 z-3" style={{ minWidth: '400px' }}>
                 <motion.div 
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="px-4 py-2 rounded-pill shadow-lg d-flex align-items-center justify-content-between border"
                    style={{ 
                       background: 'rgba(15, 23, 42, 0.9)', 
                       backdropFilter: 'blur(10px)',
                       borderColor: 'rgba(255, 193, 7, 0.3)'
                    }}
                 >
                    <div className="d-flex align-items-center gap-3">
                       <span className="fs-2">☕</span>
                       <div className="text-start">
                          <div className="text-warning fw-bold small ls-1">MOLA ZAMANI</div>
                          <div className="text-white opacity-50 extra-small" style={{ fontSize: '0.7rem' }}>Sıralama Anlık Güncellenir</div>
                       </div>
                    </div>
                    
                    <div className="vr bg-white opacity-25 mx-3" style={{ height: 35 }}></div>
                    
                    <div className="d-flex align-items-center gap-3">
                       <div className="text-end">
                          <div className="h3 mb-0 fw-bold text-warning font-monospace">
                             {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                          </div>
                       </div>
                       <div className="bg-warning rounded-circle p-1 animate-pulse" style={{ width: 8, height: 8 }}></div>
                    </div>
                 </motion.div>
              </div>


              {/* İnce İlerleme Çubuğu */}
              <div className="position-absolute top-0 start-0 w-100 z-3" style={{ height: '4px' }}>
                 <motion.div 
                    className="bg-warning h-100"
                    initial={{ width: '100%' }}
                    animate={{ width: `${(timer / 180) * 100}%` }}
                    transition={{ duration: 1, ease: "linear" }}
                 />
              </div>
           </div>
        );
     }

     // Geri sayım ekranı (Arka plan hazırlığı)
     if (gameState === 'COUNTDOWN') {
        return (
           <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-dark text-white text-center p-5" style={{ background: '#0a0f1a' }}>
              <div className="opacity-0">Hazırlanıyor...</div>
           </div>
        );
     }

    if (!currentQuestion) return <div className="p-5 text-center">Yükleniyor...</div>;

    return (
      <div className="py-2 h-100 d-flex flex-column">
        {/* Top Info Bar */}
        <Card className="custom-card mb-4">
          <Card.Body className="py-3 px-4">
             <Row className="align-items-center">
                <Col md={3} className="border-end d-flex flex-column align-items-center">
                   <small className="text-secondary fw-bold" style={{letterSpacing: '1px'}}>ODA</small>
                   <span className="h4 mb-0 fw-bold font-monospace">{roomCode}</span>
                   <small className="text-muted">
                      {Number(jokerLimit) > 0
                         ? `Joker hakkı: ${jokerLimit}`
                         : 'Joker: sınırsız'}
                   </small>
                </Col>
                <Col md={3} className="border-end d-flex flex-column align-items-center">
                   <small className="text-secondary fw-bold" style={{letterSpacing: '1px'}}>SORU</small>
                   <span className="h4 mb-0 fw-bold text-primary">
                      {currentQuestionIndex + 1} <span className="text-muted fs-6">/ {totalQuestions}</span>
                   </span>
                </Col>
                <Col md={3} className="border-end d-flex flex-column align-items-center">
                    <small className="text-secondary fw-bold" style={{letterSpacing: '1px'}}>CEVAPLAYAN</small>
                    <span className="h4 mb-0 fw-bold d-flex align-items-center gap-2">
                       <Users size={20} className="text-muted" /> {liveStats?.answered || 0} / {players.length}
                    </span>
                </Col>
                <Col md={3} className="d-flex justify-content-center">
                   <div className={`d-flex align-items-center gap-2 h2 mb-0 font-monospace fw-bold ${timer < 10 ? 'text-danger' : 'text-dark'}`}>
                      <Clock size={32} />
                      00:{timer.toString().padStart(2, '0')}
                   </div>
                </Col>
             </Row>
          </Card.Body>
        </Card>

        <Row className="flex-fill gx-4">
           {/* Main Question Display */}
           <Col lg={9} className="d-flex flex-column">
              <Card className="custom-card flex-fill overflow-hidden position-relative">
                 <div className="position-absolute top-0 end-0 p-5 opacity-10">
                    <div style={{width: 300, height: 300, background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)'}}></div>
                 </div>

                 <Card.Body className="d-flex flex-column justify-content-center p-5 position-relative z-1">
                    <div className="mb-4">
                       <Badge bg={currentQuestion.difficulty === 'EASY' ? 'success' : currentQuestion.difficulty === 'MEDIUM' ? 'warning' : 'danger'} className="me-2 px-3 py-2">
                          {currentQuestion.difficulty === 'EASY' ? 'BASİT' : currentQuestion.difficulty === 'MEDIUM' ? 'ORTA' : 'ZOR'}
                       </Badge>
                       <span className="text-secondary fw-bold small text-uppercase ls-1">
                          {currentQuestion.type === 'MULTIPLE_CHOICE' ? 'TEST SORUSU' : 'KLASİK SORU'}
                       </span>
                       {currentQuestion?.answerCount === 2 && (
                           <Badge bg="danger" className="ms-2 px-3 py-2 animate-pulse shadow-sm">
                               <Zap size={14} className="me-1" fill="white" /> 2 CEVAPLI
                           </Badge>
                       )}
                    </div>

                    {currentQuestion.media && (
                       <div className="mb-4 text-center">
                          {currentQuestion.media.type === 'IMAGE' ? (
                             <img 
                                src={currentQuestion.media.url} 
                                alt="Question Media" 
                                className="img-fluid rounded shadow-sm" 
                                style={{maxHeight: '300px', objectFit: 'contain'}} 
                             />
                          ) : (
                             <div className="bg-dark text-white rounded p-5 d-flex align-items-center justify-content-center">
                                <p className="mb-0">Video İçeriği: {currentQuestion.media.url}</p>
                             </div>
                          )}
                       </div>
                    )}

                    {currentQuestion?.answerCount === 2 && (
                        <div className="mb-4 p-3 rounded-3 bg-danger bg-opacity-10 border border-danger text-danger text-center fw-bold fs-4">
                            !! DİKKAT 2 TANE ŞIK İŞARETLEYİNİZ
                        </div>
                    )}

                    <h2 className="display-6 fw-bold text-dark mb-5 lh-base">
                       {currentQuestion.text || currentQuestion.question_text}
                    </h2>

                    <Row className="g-3">
                       {currentQuestion.type === 'MULTIPLE_CHOICE' ? (
                          // Test sorusu: seçenekleri göster, doğruları highlight et
                          (currentQuestion.options || currentQuestion.Options || []).map((opt, idx) => {
                             const isCorrect = isOptionCorrect(opt, idx);
                             const label = typeof opt === 'object' ? (opt.option_text || opt.text) : opt;
                             const optId = typeof opt === 'object' ? String(opt.id || idx) : String(idx);
                             let percentText = null;
                             if (showAnswer && revealedAnswer?.answerDistribution) {
                               const dist = revealedAnswer.answerDistribution;
                               if (dist[optId] !== undefined) {
                                 percentText = `%${dist[optId]}`;
                               }
                             }
                             
                             return (
                                <Col md={6} key={idx}>
                                   <div className={`p-4 rounded-3 border d-flex align-items-center gap-3 transition-all ${
                                      showAnswer 
                                        ? (isCorrect ? 'bg-success bg-opacity-10 border-success shadow-sm' : 'bg-light border-light opacity-50') 
                                        : 'bg-light border-light'
                                   }`}>
                                      <div className={`rounded d-flex align-items-center justify-content-center fw-bold ${
                                         showAnswer && isCorrect ? 'bg-success text-white' : 'bg-white text-muted shadow-sm flex-shrink-0'
                                      }`} style={{width: 32, height: 32}}>
                                         {['A', 'B', 'C', 'D'][idx]}
                                      </div>
                                      <span className={`fw-medium fs-5 flex-grow-1 ${showAnswer && isCorrect ? 'text-success' : 'text-dark'}`}>
                                         {label}
                                      </span>
                                      {showAnswer && percentText && (
                                         <Badge bg={isCorrect ? "success" : "secondary"} className="fs-6 px-3 py-2 rounded-pill shadow-sm">
                                            {percentText}
                                         </Badge>
                                      )}
                                      {isCorrect && showAnswer && <CheckCircle size={24} className="ms-2 text-success flex-shrink-0" />}
                                   </div>
                                </Col>
                             );
                          })
                       ) : (
                          // Klasik soru: doğru cevabı metin olarak göster
                          <Col xs={12}>
                             <div className={`p-5 rounded-3 text-center transition-all ${
                                showAnswer ? 'bg-success bg-opacity-10 border border-success' : 'bg-light border border-dashed'
                             }`}>
                                {showAnswer ? (
                                   <div>
                                      <CheckCircle size={32} className="text-success mb-3" />
                                      <h3 className="fw-bold text-success mb-0">
                                         {getCorrectAnswerText()}
                                      </h3>
                                   </div>
                                ) : (
                                    <div className="d-flex flex-column align-items-center gap-2">
                                       <div className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-3 py-1 mb-2">MODERATÖR GÖRÜNÜMÜ</div>
                                       <h3 className="fw-bold text-muted mb-0 opacity-75">
                                          Cevap Gizli
                                       </h3>
                                       <small className="text-secondary mt-2"><HelpCircle size={14} className="me-1"/> Cevabı göstermek için "Cevabı Göster" butonuna basın</small>
                                    </div>
                                )}
                             </div>
                          </Col>
                       )}
                    </Row>

                    {/* Cevap açıklanınca bilgi notu */}
                    {showAnswer && (
                       <div className="mt-4 p-3 bg-success bg-opacity-10 border border-success rounded text-success text-center">
                          <h4 className="fw-bold mb-2">Doğru Cevap: {getCorrectAnswerText()}</h4>
                          <small className="fw-bold text-secondary">✅ Cevap açıklandı — oyuncular sonuçlarını görüyor. Devam etmek için "Sıradaki Soru" butonuna basın.</small>
                       </div>
                    )}
                 </Card.Body>
              </Card>
           </Col>

           {/* Sidebar Controls */}
           <Col lg={3}>
              <div className="h-100 d-flex flex-column gap-3">
                 {/* <Card className="custom-card">
                    <Card.Header className="bg-white py-3 border-0">
                       <h6 className="mb-0 fw-bold text-secondary small" style={{letterSpacing: '1px'}}>KONTROLLER</h6>
                    </Card.Header>
                    <Card.Body className="pt-0">
                       <Button 
                          variant={isTimerRunning ? "outline-warning" : "success"}
                          size="lg"
                          className="w-100 mb-3 fw-bold d-flex align-items-center justify-content-center gap-2 border-0"
                          onClick={toggleTimer}
                          disabled={showAnswer}
                       >
                          <span className="position-relative z-1 d-flex align-items-center gap-2">
                              {isTimerRunning ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}
                              {isTimerRunning ? 'Durdur' : 'Başlat'}
                          </span>
                       </Button>

{/* <Row className="g-2">
                          <Col xs={6}>
                             <Button variant="light" className="w-100 text-muted fw-bold border-0" onClick={() => addTime(10)} disabled={showAnswer}>+10sn</Button>
                          </Col>
                          <Col xs={6}>
                             <Button variant="light" className="w-100 text-muted fw-bold border-0" onClick={() => addTime(-5)} disabled={showAnswer}>-5sn</Button>
                          </Col>
                       </Row> */}
                    {/* </Card.Body>
                 </Card> */}
                 
                 <Card className="custom-card">
                    <Card.Body>
                       <Button
                          className="w-100 mb-2 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 border-0"
                          style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}
                          onClick={revealAnswer}
                          disabled={showAnswer}
                       >
                          <Eye size={20} />
                          Cevabı Göster
                       </Button>

                       <Button
                          variant="dark"
                          className="w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 border-0"
                          onClick={nextQuestion}
                       >
                          Sıradaki Soru <FastForward size={20} />
                       </Button>
                    </Card.Body>
                 </Card>

                 <div className="flex-fill">
                    <Leaderboard players={players} showAnswer={showAnswer} />
                 </div>

                 <Card className="custom-card border-0 shadow-sm mt-auto" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}>
                    <Card.Body>
                       <Button
                          variant="link"
                          className="w-100 text-danger text-decoration-none fw-bold d-flex align-items-center justify-content-center gap-2"
                          onClick={() => {
                             Swal.fire({
                                title: 'Yarışmayı sonlandırmak istediğinize emin misiniz?',
                                text: "Bu işlem geri alınamaz ve tüm oyuncular için yarışma biter!",
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#ef4444',
                                cancelButtonColor: '#6c757d',
                                confirmButtonText: 'Evet, Sonlandır',
                                cancelButtonText: 'Vazgeç'
                             }).then((result) => {
                                if (result.isConfirmed) {
                                   handleTerminate();
                                }
                             })
                          }}
                       >
                          <XCircle size={18} />
                          Yarışmayı Sonlandır
                       </Button>
                    </Card.Body>
                 </Card>
              </div>
           </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      {renderContent()}
      <CountdownOverlay countdown={countdown} />
    </>
  );
};

export default ModeratorPanel;
